import { ReactNode, useEffect, useRef } from "react";
import {
  FloatingArrow,
  FloatingPortal,
  Placement,
  arrow,
  flip,
  offset,
  shift,
  useFloating,
  useTransitionStyles,
} from "@floating-ui/react";
import cx from "classnames";

import { TextAlign } from "types/TextAlign";
import { withTextAlign } from "utils/withTextAlign";

import patchedAutoUpdate from "./patchedAutoUpdate";
import styles from "./styles.module.css";
import { TooltipProps } from "./types";

const TRANSITION_DURATION = 100;

type TooltipFloatingProps = {
  reference: HTMLElement;
  open: boolean;
  tooltipId: string;
  children: ReactNode;
  placement: Placement;
  widthMode?: TooltipProps["widthMode"];
  textAlign: TextAlign;
  tooltipOffset: number;
  mode: "portal" | "parent";
  onExited: () => void;
};

/**
 * The actual floating element. It owns the floating-ui positioning machinery
 * (`useFloating` + `autoUpdate` + transition) and is only mounted while the tooltip
 * is open (plus the exit transition). It positions against an external reference
 * element so the trigger DOM node, owned by the wrapper, is never recreated.
 */
const TooltipFloating = ({
  reference,
  open,
  tooltipId,
  children,
  placement,
  widthMode,
  textAlign,
  tooltipOffset,
  mode,
  onExited,
}: TooltipFloatingProps) => {
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    placement,
    open,
    elements: { reference },
    middleware: [
      offset(tooltipOffset),
      shift({ padding: 8 }),
      flip(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: patchedAutoUpdate,
  });

  const { isMounted, styles: tooltipStyles } = useTransitionStyles(context, {
    duration: TRANSITION_DURATION,
  });

  useEffect(() => {
    if (!isMounted) {
      onExited();
    }
  }, [isMounted, onExited]);

  if (!isMounted) {
    return null;
  }

  const tooltip = (
    <span
      role="tooltip"
      id={tooltipId}
      className={cx(
        styles.floatingTooltip,
        widthMode && styles[widthMode],
        withTextAlign(styles, textAlign)
      )}
      ref={refs.setFloating}
      style={{ ...floatingStyles, ...tooltipStyles }}
    >
      {children}
      <FloatingArrow ref={arrowRef} context={context} className={styles.floatingArrow} />
    </span>
  );

  if (mode === "parent") {
    return tooltip;
  }

  return <FloatingPortal id="portal-root">{tooltip}</FloatingPortal>;
};

TooltipFloating.displayName = "DS.TooltipFloating";

export default TooltipFloating;
